import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { Tooltip } from "react-tooltip";
import Loader from "app/components/common/Loader";
import { RootState } from "app/lib/store/store";
import { useDispatch, useSelector } from "react-redux";
import { useFetchVehicleBrandById } from "app/hooks/vehicleInfo/useFetchVehicleBrandById";
import AddManuallyModel from "./AddManuallyModel";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/loanTypesSteps";

import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import SubTitle from "app/components/common/typography/SubTitle";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import SearchInput from "app/components/common/inputs/SearchInput";
import SelectField from "app/components/common/inputs/SelectField";
import NoConnection from "app/assets/icons/NoConnection";

interface FormData {
  model: string;
  year: string | number;
}

const SelectModel: React.FC = () => {
  const dispatch = useDispatch();
  const [selected, setSelected] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const { loanApplication, vehicleDetail, ...remaingData } = useSelector(
    (state: RootState) => state.loanTypesSteps.stepsData
  );
  const modelName = vehicleDetail?.make ?? "";
  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    control,
    formState: { errors },
    reset,
    setError,
  } = useForm<FormData>({
    defaultValues: {
      model: "",
    },
  });
  const currentYear = new Date().getFullYear();
  const yearsOptions = Array.from({ length: 13 }, (_, i) => {
    const year = currentYear - i;
    return { value: year, label: year.toString() };
  });
  const minYear = currentYear - 13;

  const { data: brands, isLoading } = useFetchVehicleBrandById(
    vehicleDetail?.makeId ?? ""
  );
  const onSubmit = (data: FormData) => {
    const { model, year } = data;

    if (!model) {
      return setError("model", {
        type: "manual",
        message: "Please select a model",
      });
    }
    dispatch(
      addStepsData({
        ...remaingData,
        loanApplication: {
          ...loanApplication,
          loanTerm: Number(0),
        },
        vehicleDetail: {
          ...vehicleDetail,
          model,
          manuallyModel: false,
          year: Number(year),
        },
      })
    );
    dispatch(nextStep());
  };

  const handleLoanSelect = (type: string) => {
    setValue("model", type);
    setSelected(type);
    clearErrors("model");
  };

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  useEffect(() => {
    if (!vehicleDetail) return;
    const { model, year } = vehicleDetail ?? {};

    if (model || year) {
      setSearchTerm(model ?? "");
      setSelected(model ?? "");
      reset({ model, year: Number(year) });
    }
  }, [vehicleDetail, reset]);

  const filteredBrands = useMemo(() => {
    if (!searchTerm.trim()) return [];
    const brandsArray = Array.isArray(brands) ? brands : [];
    return brandsArray.filter((car) =>
      car.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [brands, searchTerm]);

  const handleEnterManually = () => {
    dispatch(
      addStepsData({
        ...remaingData,
        vehicleDetail: {
          ...vehicleDetail,
          manuallyModel: true,
        },
      })
    );
  };

  const modelLabel = modelName
    ? `Search model from (${modelName})`
    : "Search model";

  return (
    <>
      {!vehicleDetail?.manuallyModel && (
        <>
          <ProgressBar progress={30} />
          <div className="w-full  sm:w-[70%] xl:w-[45%] mx-auto text-center px-4 sm:px-0">
            <MainTitle title="Select Model" />
            <SubTitle title="Search for your preferred vehicle model. If you're unsure, you can select from the available options." />

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="sm:w-[49%] w-full text-left">
                  <SearchInput
                    debounceTimeout={500}
                    placeholder={`Search Model`}
                    className="sm:!w-full"
                    onChange={handleSearch}
                    error={!!errors.model}
                    label={modelLabel}
                    value={searchTerm}
                    extraLabel={
                      vehicleDetail?.brand
                        ? `(${vehicleDetail.brand
                            .charAt(0)
                            .toUpperCase()}${vehicleDetail.brand.slice(1)})`
                        : ""
                    }
                  />
                  {errors.model && (
                    <ErrorsMessage
                      title={errors.model.message}
                      className="text-right"
                    />
                  )}
                  <input
                    type="hidden"
                    {...register("model", {
                      required: "Please select Model",
                    })}
                  />
                </div>
                <SelectField
                  label="Year"
                  name="year"
                  control={control}
                  options={yearsOptions}
                  placeholder="please select year"
                  error={errors.year?.message}
                  required={true}
                  rules={{
                    validate: (value: string | number) => {
                      const year = Number(value);
                      if (!year) return "Please select a valid year";
                      if (year < minYear || year > currentYear) {
                        return `Please select a year between ${minYear} and ${currentYear}`;
                      }
                      return true;
                    },
                  }}
                  className="w-full sm:w-[49%] sm:block"
                  removePosition={true}
                />
              </div>

              <div className="mt-6">
                {isLoading ? (
                  <Loader />
                ) : filteredBrands.length === 0 ? (
                  searchTerm.trim() === "" ? (
                    <div className="flex flex-wrap justify-center my-10 text-center  mx-auto ">
                      <NoConnection />
                      <p className="text-center text-base text-[#585C7B] w-full mt-3 font-Mulish">
                        Please search to view Model
                      </p>
                    </div>
                  ) : (
                    <p className="text-center font-Mulish text-base text-[#585C7B] w-full mt-14">
                      couldn’t find your car?{" "}
                      <span
                        className="text-[#D1A960] cursor-pointer"
                        onClick={handleEnterManually}
                      >
                        Enter manually
                      </span>
                    </p>
                  )
                ) : (
                  <div
                    className="
            max-h-[300px] overflow-y-auto"
                  >
                    <div className="flex flex-wrap justify-center   gap-2 sm:gap-4 w-full">
                      {filteredBrands?.map((car) => {
                        const isSelected = car.name === selected;
                        const tooltipId = `tooltip-${car.id}`;
                        return (
                          <>
                            <div
                              key={car.id}
                              onClick={() => handleLoanSelect(car.name)}
                              className={`w-2/5 sm:w-1/2 md:w-1/3 lg:w-[23%]  h-[80px] flex justify-center align-center flex-col  text-center border  border-gray-300  hover:shadow-cardShadow rounded-[10px] cursor-pointer mb-4 px-2 ${
                                isSelected
                                  ? "bg-primary"
                                  : "bg-[#F5F6F7] text-[#313131]"
                              }`}
                              data-tooltip-id={tooltipId}
                              data-tooltip-content={car.name}
                            >
                              <h6
                                className={`truncate-multiline font-Mulish text-[16px] text-center capitalize font-semibold   break-words ${
                                  isSelected ? "text-white" : "text-darkgrey"
                                }`}
                              >
                                {car.name}
                              </h6>
                            </div>
                            <Tooltip
                              id={tooltipId}
                              place="top"
                              className="font-Mulish"
                              style={{
                                backgroundColor: "#D1A960",
                                color: "#fff",
                                fontSize: "14px",
                                padding: "8px 12px",
                                borderRadius: "8px",
                                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                                border: "1px solid #E5E7EB",
                                maxWidth: "280px",
                                fontWeight: 400,
                                zIndex: 999,
                              }}
                            />
                          </>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <input
                type="hidden"
                {...register("model", {
                  required: "Please select Model",
                })}
              />

              <div className="flex justify-between my-10">
                <PreviousButtom onClick={() => dispatch(prevStep())}>
                  Previous
                </PreviousButtom>
                <ContinueButton type="submit">Next</ContinueButton>
              </div>
            </form>
          </div>
        </>
      )}
      {vehicleDetail?.manuallyModel && <AddManuallyModel />}
    </>
  );
};

export default SelectModel;
